import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Monitor, X, Loader2, AlertTriangle, Copy, Check, ExternalLink, RefreshCw, Wifi, WifiOff, Maximize2, Minimize2, ChevronDown, ChevronUp, VideoOff, MousePointer2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// STUN servers (should match backend)
const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
];

// Cursor send throttle (ms) - ~30fps
const CURSOR_THROTTLE_MS = 33;

/**
 * Admin viewer for screen share sessions.
 * Displays the shared screen from the tenant via WebRTC.
 * Features: Maximize/Restore, Pop-out, Responsive layout, Cursor overlay
 */
export const AdminScreenShareViewer = ({ 
  isOpen, 
  onClose, 
  sessionData,
  onSessionEnd 
}) => {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [error, setError] = useState(null);
  const [tenantConnected, setTenantConnected] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [inviteLinkCollapsed, setInviteLinkCollapsed] = useState(false);
  const [cursorEnabled, setCursorEnabled] = useState(false); // Tenant has enabled cursor
  
  // Refs
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const lastCursorSendRef = useRef(0);
  
  // Build the full invite URL
  const inviteUrl = sessionData?.tenant_join_url 
    ? `${BACKEND_URL}${sessionData.tenant_join_url}`
    : '';
  
  // Copy invite link to clipboard
  const copyInviteLink = useCallback(() => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    toast.success('Invite link copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  }, [inviteUrl]);
  
  // Pop-out to new window/tab
  const handlePopOut = useCallback(() => {
    const popOutUrl = `/admin/support/session/${sessionData?.session_id}?token=${encodeURIComponent(sessionData?.admin_view_token || '')}`;
    window.open(popOutUrl, '_blank', 'noopener,noreferrer');
  }, [sessionData]);
  
  // Clean up connections
  const cleanup = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setConnectionStatus('disconnected');
    setTenantConnected(false);
  }, []);
  
  // Initialize WebSocket and WebRTC when dialog opens
  useEffect(() => {
    if (!isOpen || !sessionData?.admin_view_token || !sessionData?.session_id) {
      return;
    }
    
    setConnectionStatus('connecting');
    setError(null);
    
    // Connect to WebSocket for signaling
    const wsUrl = `${WS_URL}/api/ws/support/${sessionData.session_id}?token=${sessionData.admin_view_token}&role=admin`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    
    ws.onopen = () => {
      console.log('Admin WebSocket connected');
      setConnectionStatus('waiting');
    };
    
    ws.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      
      switch (data.type) {
        case 'config':
          initializePeerConnection(data.ice_servers || ICE_SERVERS);
          break;
          
        case 'peer_joined':
          if (data.role === 'tenant') {
            setTenantConnected(true);
            setInviteLinkCollapsed(true); // Auto-collapse invite link when tenant connects
            toast.success('Tenant connected');
          }
          break;
          
        case 'peer_left':
          if (data.role === 'tenant') {
            setTenantConnected(false);
            setConnectionStatus('waiting');
            setInviteLinkCollapsed(false); // Show invite link again
            if (videoRef.current) {
              videoRef.current.srcObject = null;
            }
            toast.info('Tenant disconnected');
          }
          break;
          
        case 'offer':
          if (pcRef.current && data.sdp) {
            try {
              await pcRef.current.setRemoteDescription(new RTCSessionDescription({
                type: 'offer',
                sdp: data.sdp
              }));
              
              const answer = await pcRef.current.createAnswer();
              await pcRef.current.setLocalDescription(answer);
              
              ws.send(JSON.stringify({
                type: 'answer',
                sdp: answer.sdp
              }));
            } catch (err) {
              console.error('Failed to handle offer:', err);
              setError('Failed to establish connection');
            }
          }
          break;
          
        case 'ice_candidate':
          if (pcRef.current && data.candidate) {
            try {
              await pcRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
            } catch (err) {
              console.error('Failed to add ICE candidate:', err);
            }
          }
          break;
          
        case 'status_update':
          if (data.status === 'sharing') {
            setConnectionStatus('connected');
          }
          break;
          
        case 'sharing_stopped':
          setConnectionStatus('waiting');
          setTenantConnected(false);
          setInviteLinkCollapsed(false);
          setCursorEnabled(false);
          if (videoRef.current) {
            videoRef.current.srcObject = null;
          }
          toast.info('Tenant stopped sharing');
          break;
          
        case 'connection_failed':
          setConnectionStatus('failed');
          setError(data.reason || 'Connection failed');
          break;
          
        case 'cursor_enabled':
          // Tenant has enabled cursor overlay
          setCursorEnabled(true);
          toast.success('Cursor overlay enabled by tenant');
          break;
          
        case 'cursor_disabled':
          // Tenant has disabled cursor overlay
          setCursorEnabled(false);
          toast.info('Cursor overlay disabled by tenant');
          break;
          
        case 'error':
          setError(data.message);
          break;
          
        default:
          break;
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection error');
      setConnectionStatus('failed');
    };
    
    ws.onclose = () => {
      console.log('WebSocket closed');
    };
    
    return () => {
      cleanup();
    };
  }, [isOpen, sessionData, cleanup]);
  
  // Initialize WebRTC peer connection
  const initializePeerConnection = useCallback((iceServers) => {
    const pc = new RTCPeerConnection({
      iceServers: iceServers
    });
    pcRef.current = pc;
    
    pc.ontrack = (event) => {
      console.log('Received track:', event.streams);
      if (videoRef.current && event.streams[0]) {
        videoRef.current.srcObject = event.streams[0];
        setConnectionStatus('connected');
      }
    };
    
    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'ice_candidate',
          candidate: event.candidate.toJSON()
        }));
      }
    };
    
    pc.onconnectionstatechange = () => {
      console.log('Admin connection state:', pc.connectionState);
      switch (pc.connectionState) {
        case 'connected':
          setConnectionStatus('connected');
          break;
        case 'disconnected':
        case 'closed':
          setConnectionStatus('waiting');
          break;
        case 'failed':
          setConnectionStatus('failed');
          setError('Connection failed. The tenant may be behind a restrictive firewall.');
          break;
        default:
          break;
      }
    };
    
    pc.ondatachannel = (event) => {
      const channel = event.channel;
      channel.onmessage = (e) => {
        console.log('Data channel message:', e.data);
      };
    };
    
  }, []);
  
  // Handle mouse movement over video to send cursor position
  const handleMouseMove = useCallback((e) => {
    // Only send if cursor is enabled and we're connected
    if (!cursorEnabled || connectionStatus !== 'connected' || !wsRef.current) return;
    
    // Throttle cursor updates
    const now = Date.now();
    if (now - lastCursorSendRef.current < CURSOR_THROTTLE_MS) return;
    lastCursorSendRef.current = now;
    
    const container = videoContainerRef.current;
    if (!container) return;
    
    const rect = container.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    // Only send if within bounds
    if (x >= 0 && x <= 1 && y >= 0 && y <= 1) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'cursor_position',
          x: x,
          y: y,
          visible: true
        }));
      }
    }
  }, [cursorEnabled, connectionStatus]);
  
  // Handle mouse leave - hide cursor
  const handleMouseLeave = useCallback(() => {
    if (!cursorEnabled || !wsRef.current) return;
    
    if (wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor_position',
        x: 0,
        y: 0,
        visible: false
      }));
    }
  }, [cursorEnabled]);
  
  // Handle dialog close
  const handleClose = () => {
    cleanup();
    setIsMaximized(false);
    setCursorEnabled(false);
    onClose();
    if (onSessionEnd) {
      onSessionEnd();
    }
  };
  
  // Get status display
  const getStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return { icon: Wifi, color: 'text-green-600', bg: 'bg-green-50', text: 'Connected - Viewing Screen' };
      case 'connecting':
        return { icon: Loader2, color: 'text-yellow-600', bg: 'bg-yellow-50', text: 'Connecting...' };
      case 'waiting':
        return { icon: Monitor, color: 'text-blue-600', bg: 'bg-blue-50', text: 'Waiting for tenant to share...' };
      case 'failed':
        return { icon: WifiOff, color: 'text-red-600', bg: 'bg-red-50', text: 'Connection Failed' };
      default:
        return { icon: Monitor, color: 'text-gray-600', bg: 'bg-gray-50', text: 'Disconnected' };
    }
  };
  
  const status = getStatusDisplay();
  const StatusIcon = status.icon;
  
  // Dynamic modal classes based on maximize state
  const modalClasses = isMaximized 
    ? 'w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh]'
    : 'w-full max-w-4xl max-h-[85vh]';
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent 
        className={`${modalClasses} flex flex-col p-0 gap-0 overflow-hidden`}
        data-testid="screen-share-viewer-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0">
          <div className="flex items-center gap-3">
            <Monitor className="h-5 w-5 text-blue-600" />
            <div>
              <DialogTitle className="text-base font-semibold">
                Screen Share Session
              </DialogTitle>
              <DialogDescription className="text-xs text-gray-500">
                {sessionData?.tenant_name ? `Viewing: ${sessionData.tenant_name}` : 'Support Session'}
              </DialogDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Not recorded badge */}
            <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600 border-gray-300">
              <VideoOff className="h-3 w-3 mr-1" />
              Not recorded
            </Badge>
            
            {/* Cursor enabled indicator */}
            {cursorEnabled && connectionStatus === 'connected' && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-300">
                <MousePointer2 className="h-3 w-3 mr-1" />
                Cursor active
              </Badge>
            )}
            
            {/* Pop-out button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePopOut}
              title="Open in new tab"
              className="h-8 w-8 p-0"
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
            
            {/* Maximize/Restore button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMaximized(!isMaximized)}
              title={isMaximized ? 'Restore' : 'Maximize'}
              className="h-8 w-8 p-0"
            >
              {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            
            {/* Close button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              title="Close"
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Content area - flex column */}
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Status bar */}
          <div className={`flex items-center justify-between px-4 py-2 shrink-0 ${status.bg}`}>
            <div className="flex items-center gap-2">
              <StatusIcon className={`h-4 w-4 ${status.color} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} />
              <span className={`text-sm font-medium ${status.color}`}>
                {status.text}
              </span>
            </div>
            
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>Tenant: {tenantConnected ? 'Connected' : 'Not connected'}</span>
              <span className="text-gray-300">|</span>
              <span>
                Expires: {sessionData?.expires_at 
                  ? new Date(sessionData.expires_at).toLocaleTimeString() 
                  : 'Unknown'}
              </span>
            </div>
          </div>
          
          {/* Collapsible invite link section */}
          {connectionStatus === 'waiting' && (
            <div className="shrink-0 border-b">
              <button
                onClick={() => setInviteLinkCollapsed(!inviteLinkCollapsed)}
                className="w-full px-4 py-2 flex items-center justify-between text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium">
                  {inviteLinkCollapsed ? 'Show invite link' : 'Invite link'}
                </span>
                {inviteLinkCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
              </button>
              
              {!inviteLinkCollapsed && (
                <div className="px-4 pb-3 space-y-2 bg-gray-50">
                  <p className="text-xs text-gray-500">Share this link with the tenant:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-white border rounded text-xs truncate">
                      {inviteUrl}
                    </code>
                    <Button 
                      onClick={copyInviteLink} 
                      variant="outline" 
                      size="sm"
                      className="shrink-0 h-8"
                    >
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400">
                    The tenant will see a consent dialog before their screen is shared.
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <div className="px-4 py-2 shrink-0">
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 text-sm">
                  {error}
                  {connectionStatus === 'failed' && (
                    <div className="mt-1 text-xs">
                      <p>Suggestions: Ask tenant to try a different network, or use screenshot uploads.</p>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            </div>
          )}
          
          {/* Video display - flex-grow to fill remaining space */}
          <div 
            ref={videoContainerRef}
            className="flex-1 min-h-0 relative bg-[#0b1220] overflow-hidden"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: cursorEnabled && connectionStatus === 'connected' ? 'crosshair' : 'default' }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-contain"
              style={{ background: '#0b1220' }}
            />
            
            {/* Overlay when not connected */}
            {connectionStatus !== 'connected' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0b1220]/90 text-white">
                <StatusIcon className={`h-12 w-12 mb-4 opacity-60 ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`} />
                <p className="text-lg font-medium">{status.text}</p>
                {connectionStatus === 'waiting' && !tenantConnected && (
                  <p className="text-sm text-gray-400 mt-2 max-w-xs text-center">
                    {inviteLinkCollapsed 
                      ? 'Expand the invite link section above to share with the tenant'
                      : 'Share the invite link above with the tenant'}
                  </p>
                )}
                {connectionStatus === 'failed' && (
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="outline" 
                    className="mt-4"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                )}
              </div>
            )}
            
            {/* Cursor tracking info overlay */}
            {cursorEnabled && connectionStatus === 'connected' && (
              <div className="absolute bottom-2 right-2 px-2 py-1 bg-blue-600/80 text-white text-xs rounded flex items-center gap-1">
                <MousePointer2 className="h-3 w-3" />
                Move cursor to guide tenant
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 border-t bg-gray-50 shrink-0 text-xs text-gray-500">
            <span>Session ID: {sessionData?.session_id?.slice(0, 8)}...</span>
            <Button variant="outline" size="sm" onClick={handleClose} className="h-7 text-xs">
              End Session
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminScreenShareViewer;
